import React, { Suspense, lazy, useEffect, useState } from 'react';
import { BrowserRouter, Routes, useLocation } from 'react-router-dom';
import { useAuthTasks } from './service/authService';
import ImpersonationBanner from './component/ImpersonationBanner';
import Switch from './component/Switch';
import { trackAffiliateClick, trackMarketingEvent } from './service/marketingTracker';
import { applySeasonalThemeToDocument } from './component/themeUi';
import { LocaleProvider } from './i18n/LocaleProvider';
import { stripLocaleFromPathname } from './i18n/locale';
import { generateAppRoutes } from './routes/generateAppRoutes';

const ToastHost = lazy(() => import('./component/ToastHost'));
// Landing
const Index = lazy(() => import('./pages/landing/welcome'));
// import IndexChrist from './pages/events/christmas';

// const Csolutions = lazy(() => import('./pages/landing/csolutions')); // /contact + /custom-solutions not live
const ShoppingCart = lazy(() => import('./pages/landing/cart'));

// Legal
const LegalImprint = lazy(() => import('./pages/legal/imprint'));
const LegalFairUse = lazy(() => import('./pages/legal/fairUse'));
const LegalTco = lazy(() => import('./pages/legal/tco'));
const LegalDsa = lazy(() => import('./pages/legal/dsa'));
const LegalRco = lazy(() => import('./pages/legal/roc'));
const LegalAgb = lazy(() => import('./pages/legal/agb'));
const LegalPrivacyPolicy = lazy(() => import('./pages/legal/privacyPolicy'));

// Services
const ServiceColocation = lazy(() => import('./pages/products/colocation'));
//import ServiceDomain from './pages/services/domain';
//import ServiceIpv4 from './pages/products/rent-ipv4';
//import ServiceIpv6 from './pages/services/rent-ipv6';
//import ServiceVpn from './pages/products/vpn';
// const ServiceVpn = lazy(() => import('./pages/products/vpn'));
const RipeLir = lazy(() => import('./pages/products/ripeLir'));
const Vds = lazy(() => import('./pages/products/vds'));
const DediServer = lazy(() => import('./pages/products/dedicatedServer'));
const ServiceWeb = lazy(() => import('./pages/products/web'));
const ServiceComingSoon = lazy(() => import('./pages/products/ServiceComingSoon'));
const ServiceRemoteHands = lazy(() => import('./pages/products/remoteHands'));
const ServiceManagedHosting = lazy(() => import('./pages/products/managedHosting'));
// const ServiceCustomIntegrations = lazy(() => import('./pages/products/customIntegrations'));

// Game
const GameServers = lazy(() => import('./pages/products/game-servers/gameServers'));
const GameCsgo = lazy(() => import('./pages/products/game-servers/csgo'));
const GameMc = lazy(() => import('./pages/products/game-servers/minecraft'));
const GameFivem = lazy(() => import('./pages/products/game-servers/fivem'));
const GameRust = lazy(() => import('./pages/products/game-servers/rust'));
const GameTs = lazy(() => import('./pages/products/game-servers/teamspeak'));
const GameTerraria = lazy(() => import('./pages/products/game-servers/terraria'));
const GameArk = lazy(() => import('./pages/products/game-servers/ark'));
const GameHytale = lazy(() => import('./pages/products/game-servers/hytale'));
const GameGarrysMod = lazy(() => import('./pages/products/game-servers/garrys-mod'));
const GameEscapeFromTarkov = lazy(() => import('./pages/products/game-servers/escape-from-tarkov'));
const GameDayz = lazy(() => import('./pages/products/game-servers/dayz'));

// Blog
const BlogHome = lazy(() => import('./pages/blog/home'));
const BlogPostPage = lazy(() => import('./pages/blog/content/postPage'));

/* About Us */
// Company
const CompanyTeam = lazy(() => import('./pages/company/team'));
const CompanyPartners = lazy(() => import('./pages/company/partners'));
const CompanyPayments = lazy(() => import('./pages/company/payments'));
const CompanyReviews = lazy(() => import('./pages/company/reviews'));
const CompanyCareers = lazy(() => import('./pages/company/careers'));
// Network
const NetworkDProt = lazy(() => import('./pages/network/ddosprot'));
const NetworkSPing = lazy(() => import('./pages/network/smokeping'));
const NetworkLGlass = lazy(() => import('./pages/network/lookingglass'));
const NetworkSMonitor = lazy(() => import('./pages/network/uptime'));
const NetworkTransit = lazy(() => import('./pages/products/transit'));
const NetworkPeering = lazy(() => import('./pages/network/peering'));
const NetworkSla = lazy(() => import('./pages/network/sla'));
// Datacenter
const DcHardware = lazy(() => import('./pages/datacenter/hardware'));
const DcLocation = lazy(() => import('./pages/datacenter/location'));
// Reseller
const ResellerApply = lazy(() => import('./pages/reseller/apply'));

/* Debugging */
// import PageChange from './pages/docs/changelog';
// import PageDoc from './pages/docs/documentation';
// import PageUI from './pages/docs/uiComponents';
//import PageThankyou from './pages/error/pageThankyou';
//import PageMaintenance from './pages/error/pageMaintenance';
const PageError = lazy(() => import('./pages/error/pageError'));
const PageDomainDisabled = lazy(() => import('./pages/error/pageDomainDisabled'));
const PageDomainNotFound = lazy(() => import('./pages/error/pageDomainNotFound'));
const PageDomainNonexistent = lazy(() => import('./pages/error/pageDomainNonexistent'));
//import PageComingsoon from './pages/error/pageComingsoon';

const routeComponents = {
    Index,
    ShoppingCart,
    LegalImprint,
    LegalFairUse,
    LegalAgb,
    LegalPrivacyPolicy,
    LegalRco,
    LegalTco,
    LegalDsa,
    // Csolutions,
    RipeLir,
    Vds,
    DediServer,
    ServiceWeb,
    ServiceComingSoon,
    ServiceRemoteHands,
    ServiceManagedHosting,
    // ServiceCustomIntegrations,
    ServiceColocation,
    PageDomainDisabled,
    PageDomainNotFound,
    PageDomainNonexistent,
    GameServers,
    GameMc,
    GameCsgo,
    GameFivem,
    GameRust,
    GameTs,
    GameTerraria,
    GameArk,
    GameHytale,
    GameGarrysMod,
    GameEscapeFromTarkov,
    GameDayz,
    NetworkTransit,
    CompanyTeam,
    CompanyPartners,
    CompanyPayments,
    CompanyReviews,
    CompanyCareers,
    NetworkDProt,
    NetworkSPing,
    NetworkLGlass,
    NetworkSMonitor,
    NetworkPeering,
    NetworkSla,
    DcHardware,
    DcLocation,
    ResellerApply,
    BlogHome,
    BlogPostPage,
    PageError,
};

function MarketingRouteTracker() {
    const location = useLocation();

    const getProductCategoryByPath = (pathname) => {
        const path = stripLocaleFromPathname(pathname);
        if (path.startsWith('/services/dedicated-servers') || path.startsWith('/dedicated-servers')) return 'dedicated';
        if (path.startsWith('/services/web-hosting') || path.startsWith('/web-hosting')) return 'web-hosting';
        if (
          path.startsWith('/services/vps-kvm')
          || path.startsWith('/services/vds')
          || path.startsWith('/vps-kvm')
          || path.startsWith('/kvm')
        ) return 'virtual-server';
        if (path.startsWith('/services/vpn') || path.startsWith('/vpn')) return 'vpn';
        if (path.startsWith('/services/colocation') || path.startsWith('/colocation')) return 'colocation';
        if (path.startsWith('/services/ripe-lir') || path.startsWith('/ripe-lir')) return 'lir-service';
        if (path.startsWith('/services/ip-transit') || path.startsWith('/ip-transit')) return 'ip-transit';
        if (path.startsWith('/game-server') || path.startsWith('/game-servers') || path.startsWith('/services/game-server')) {
          return 'game-server';
        }
        return null;
    };

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const pathKey = `${location.pathname}${location.search}`;
        const sessionKey = `mkt_route_${pathKey}`;
        if (sessionStorage.getItem(sessionKey)) return;
        sessionStorage.setItem(sessionKey, '1');
        trackMarketingEvent('visit', { pagePath: pathKey });
        const affiliateCode = new URLSearchParams(location.search).get('aff');
        if (affiliateCode) trackAffiliateClick(affiliateCode);

        const category = getProductCategoryByPath(location.pathname);
        if (category) {
            const productSessionKey = `mkt_product_${category}_${location.pathname}`;
            if (!sessionStorage.getItem(productSessionKey)) {
                sessionStorage.setItem(productSessionKey, '1');
                trackMarketingEvent('click', {
                    pagePath: location.pathname,
                    funnelStep: 'product_view',
                    productType: category,
                });
            }
        }
    }, [location.pathname, location.search]);

    return null;
}


export default function App() {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [toastReady, setToastReady] = useState(false);
    useAuthTasks();
    useEffect(() => {
        //let mte = "";
        // Check if dark mode is active on the document's root element
        const checkDarkMode = () => {
            setIsDarkMode(document.documentElement.classList.contains('dark'));
        };

        checkDarkMode(); // Initial check

        // Observe changes to the document's class list
        const observer = new MutationObserver(checkDarkMode);
        observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        applySeasonalThemeToDocument();
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const enable = () => setToastReady(true);

        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(enable, { timeout: 2500 });
            return undefined;
        }

        const timer = setTimeout(enable, 1200);
        return () => clearTimeout(timer);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const path = stripLocaleFromPathname(window.location.pathname);
        const needsMdiStyles =
            path.startsWith('/game-server/') ||
            path.startsWith('/services/vpn') ||
            path === '/network/ddos-protection';

        if (!needsMdiStyles) return undefined;

        const loadMdi = () => import('./assets/libs/@mdi/font/css/materialdesignicons.min.css');
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(loadMdi, { timeout: 2000 });
        } else {
            const timer = setTimeout(loadMdi, 1200);
            return () => clearTimeout(timer);
        }
    }, []);

    return (
        <BrowserRouter>
            <LocaleProvider>
                <ImpersonationBanner />
                <MarketingRouteTracker />
                {toastReady ? (
                    <Suspense fallback={null}>
                        <ToastHost isDarkMode={isDarkMode} />
                    </Suspense>
                ) : null}
                <Switch />
                <main id="main-content">
                    <Suspense fallback={null}>
                        <Routes>
                            {generateAppRoutes(routeComponents)}
                        </Routes>
                    </Suspense>
                </main>
            </LocaleProvider>
        </BrowserRouter>
    );
}
